import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import jsPDF from "jspdf";

import shippingLabelSample from "../assets/shippin-label-sample.png";
import AlertPopup from "../components/AlertPopup";

export default function ShipItem() {
  const { orderId } = useParams();

  // State for AlertPopup
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sample order data - replace with actual data
  const [orderData] = useState({
    orderId: orderId || "ORD-123456",
    trackingNumber: "1Z999AA1234567890",
  });

  // Print shipping label
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>UPS Shipping Label - ${orderData.orderId}</title>
          <style>
            body { margin: 0; padding: 20px; }
            img { max-width: 100%; height: auto; }
            @media print {
              body { margin: 20; padding: 0; }
              img { width: 4in; height: 6in; }
            }
          </style>
        </head>
        <body>
          <img src="${shippingLabelSample}" alt="UPS Shipping Label" />
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Download shipping label as PDF using canvas
  const handleDownloadPDF = async () => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "in",
        format: [4, 6], // Standard shipping label size (4x6 inches)
      });

      // Create an image element to load the shipping label
      const img = new Image();

      // Create a promise to handle image loading
      const loadImage = new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = (error) => {
          console.error("Image loading failed:", error);
          reject(new Error("Failed to load shipping label image"));
        };

        // Set the image source
        img.src = shippingLabelSample;
      });

      try {
        // Wait for image to load
        await loadImage;

        // Create canvas and draw the image
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas dimensions to match the image
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        // Draw the image on canvas
        ctx.drawImage(img, 0, 0);

        // Convert canvas to data URL
        const imgData = canvas.toDataURL("image/png", 1.0); // High quality

        // Add the image data to PDF
        pdf.addImage(imgData, "PNG", 0, 0, 4, 6);

        // Save the PDF
        pdf.save(
          `UPS_Label_${orderData.orderId}_${
            new Date().toISOString().split("T")[0]
          }.pdf`
        );

        // Clean up
        canvas.remove();
      } catch (imageError) {
        // Fallback: Create a text-based PDF if image fails
        console.warn("Using fallback PDF generation:", imageError.message);

        pdf.setFontSize(16);
        pdf.text("UPS Shipping Label", 0.5, 1);
        pdf.setFontSize(12);
        pdf.text(`Order ID: ${orderData.orderId}`, 0.5, 1.5);
        pdf.text(`Tracking: ${orderData.trackingNumber}`, 0.5, 2);
        pdf.text("Image could not be loaded.", 0.5, 2.5);
        pdf.text("Please print from the web interface.", 0.5, 3);

        pdf.save(
          `UPS_Label_${orderData.orderId}_${
            new Date().toISOString().split("T")[0]
          }.pdf`
        );
      }
    } catch (error) {
      console.error("PDF generation failed:", error);
      setAlertMessage(
        "Failed to generate PDF. Please try again or print directly from the page."
      );
      setAlertOpen(true);
    }
  };

  // Static steps data
  const steps = [
    {
      id: 1,
      title: "Order Confirmed",
      description: "Buyer has completed payment",
    },
    {
      id: 2,
      title: "Create Label",
      description: "Generate your UPS shipping label",
    },
    {
      id: 3,
      title: "Ship Item",
      description: "Pack and hand over to UPS",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Ship Item - {orderData.orderId} | R3PLAY</title>
        <meta name="description" content="Ship your item with UPS" />
      </Helmet>

      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: "1440px",
            px: "100px",
            "@media (max-width: 1024px)": {
              px: "16px",
            },
          }}
        >
          <Box sx={{ boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.08)" }}>
            {/* Header */}
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                py: 2,
              }}
            >
              {/* Order Status */}
              <Box sx={{ px: 3 }}>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "#000",
                  }}
                >
                  Ship Item
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#666",
                    mb: 2,
                  }}
                >
                  Pack and hand over to UPS
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {/* Progress Steps */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 3,
                  justifyContent: "center",
                  "@media (max-width: 768px)": {
                    flexDirection: "column",
                    gap: 2,
                  },
                }}
              >
                {steps.map((step, index) => {
                  const isCompletedStep = true; // All steps are completed
                  return (
                    <React.Fragment key={step.id}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: "#EF5658",
                          borderRadius: "40px",
                          px: 2,
                          width: "287px",
                          height: "58px",
                          "@media (max-width: 768px)": {
                            width: "329px",
                          },
                        }}
                      >
                        {/* Icon with circle background */}
                        <Box
                          sx={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "#F35D5F",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                            flexShrink: 0,
                          }}
                        >
                          <CheckOutlinedIcon
                            sx={{
                              color: "#FFFFFF",
                              fontSize: "24px",
                            }}
                          />
                        </Box>

                        {/* Text */}
                        <Box sx={{ flex: 1, alignItems: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#FFFFFF",
                              lineHeight: 1.5,
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#FFFFFF",
                              lineHeight: 1.8,
                              "@media (min-width: 768px) and (max-width: 1160px)": {
                                display: "none",
                              },
                            }}
                          >
                            {step.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Horizontal Divider between steps */}
                      {index < steps.length - 1 && (
                        <Box
                          sx={{
                            width: "40px",
                            height: "1px",
                            backgroundColor: "rgba(0, 0, 0, 0.12)",
                            "@media (max-width: 768px)": {
                              display: "none", // Hide dividers on mobile
                            },
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>

            {/* Main Content Area */}
            <Box
              sx={{
                backgroundColor: "#F0F0F0",
                p: 3,
                "@media (max-width: 768px)": {
                  p: 2,
                },
              }}
            >
              {/* Centered Card Layout */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    p: 3,
                    width: "840px",
                    "@media (max-width: 856px)": {
                      width: "100%",
                    },
                  }}
                >
                  {/* Header */}
                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: 600,
                      color: "#000",
                      mb: 1,
                    }}
                  >
                    Ship your item with UPS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#666",
                      mb: 3,
                    }}
                  >
                    Your UPS shipping label is ready. Print and securely attach
                    it to your package, then drop it off at any UPS location.
                    Once UPS receives the package, you can track delivery status
                    from the Orders section.
                  </Typography>

                  {/* Shipping Label Display */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={shippingLabelSample}
                      alt="UPS Shipping Label"
                      sx={{
                        width: "100%",
                        maxWidth: "400px",
                        height: "auto",
                        borderRadius: "4px",
                        border: "1px solid #E0E0E0",
                        mb: 2,
                      }}
                    />
                    {/* Action Buttons */}
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "400px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<FileDownloadIcon />}
                        onClick={handleDownloadPDF}
                        sx={{
                          backgroundColor: "#EF5658",
                          color: "#FFFFFF",
                          fontWeight: 600,
                          fontSize: "15px",
                          py: 1.5,
                          borderRadius: "8px",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#D32F2F",
                          },
                        }}
                      >
                        Download Shipping Label
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={
                          <PrintOutlinedIcon sx={{ color: "#EF5658" }} />
                        }
                        onClick={handlePrint}
                        sx={{
                          borderColor: "#EF5658",
                          color: "#EF5658",
                          fontWeight: 600,
                          fontSize: "15px",
                          py: 1.5,
                          borderRadius: "8px",
                          textTransform: "none",
                          "&:hover": {
                            borderColor: "#D32F2F",
                            backgroundColor: "rgba(239, 86, 88, 0.04)",
                          },
                          "@media (max-width: 1024px)": {
                            display: "none",
                          },
                        }}
                      >
                        Print
                      </Button>
                    </Box>
                  </Box>

                  {/* Tracking Tip */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 2,
                      p: 2,
                      backgroundColor: "#F6F9FD",
                      borderRadius: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        backgroundColor: "#EF5658",
                        color: "#FFFFFF",
                        fontSize: "12px",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.2,
                      }}
                    >
                      !
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#000",
                          mb: 0.5,
                          mr: 0.5,
                        }}
                      >
                        Tip:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        Save your tracking number for reference (e.g.,{" "}
                        {orderData.trackingNumber}).
                      </Typography>
                    </Box>
                  </Box>

                  {/* Drop-off Location */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 2,
                      border: "1px solid #E1E1E1",
                      borderRadius: "8px",
                      "@media (min-width: 768px)": {
                        alignItems: "center",
                      },
                      "@media (max-width: 768px)": {
                        flexDirection: "column",
                      },
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#000",
                          mb: 0.5,
                        }}
                      >
                        Drop-off Location
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        Locate a nearby UPS store or authorized drop-off point.
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "#EF5658",
                          color: "#FFFFFF",
                          fontWeight: 600,
                          fontSize: "15px",
                          py: 1,
                          px: 2,
                          textTransform: "uppercase",
                          "&:hover": {
                            backgroundColor: "#D32F2F",
                          },
                          "@media (max-width: 768px)": {
                            mt: 2,
                          },
                        }}
                        //onClick={() => window.open("https://www.ups.com/dropoff", "_blank")}
                      >
                        Find a UPS Location
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Alert Popup */}
      <AlertPopup
        open={alertOpen}
        title={alertMessage}
        confirmText="OK"
        cancelText=""
        onConfirm={() => setAlertOpen(false)}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
}
